import { useEffect, useState } from "react";
import { ui, nextLang, useSiteLang, setSiteLang } from "./siteI18n";
import { Link, useParams, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { api } from "@/lib/api";
import { RenderBlocks, tx } from "./blocks";
import { MegaMenus } from "./Catalog";
import { ChatWidget } from "@/views/web/PublicWeb";
import "./site.css";
import { BrandLogo } from "@/components/BrandLogo";


export function useSiteData(siteSlug) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { api.get(`/site/pages${siteSlug ? `?site=${siteSlug}` : ""}`).then((r) => setData(r.data)).catch((e) => setErr(e)); }, [siteSlug]);
  return { data, err };
}

export function SiteFrame({ site, pages, lang, setLang, base, children, preview }) {
  const [open, setOpen] = useState(false);
  const theme = site?.theme || {};
  const to = (href) => (href.startsWith("/login") || href.startsWith("/signup") || href.startsWith("http") ? href : `${base}${href}`.replace(/\/+/g, "/"));
  const NavA = ({ n }) => (n.href.includes("#") ? <a href={to(n.href)} className="site-nav-link">{tx(n.label, lang)}</a> : <Link to={to(n.href)} className="site-nav-link" onClick={() => setOpen(false)}>{tx(n.label, lang)}</Link>);
  const nav = site?.nav || [];
  return (
    <div className={`site-root ${theme.skin ? `skin-${theme.skin}` : ""}`} style={{ "--site-primary": theme.primary || "#0f766e", "--site-accent": theme.accent || "#f59e0b" }} data-testid="public-site">
      <header className={`site-header ${preview ? "" : "sticky top-0 z-40"}`}>
        <div className="site-container h-16 flex items-center gap-6">
          <BrandLogo name={site?.name || "Orbis ERP"} tone="site" size="md" to={base || "/"} testId="site-logo" />
          <nav className="hidden md:flex items-center gap-1 ml-6" data-testid="site-nav">{base === "" && <MegaMenus lang={lang} base="" />}{nav.map((n, i) => <NavA key={i} n={n} />)}</nav>
          <div className="ml-auto flex items-center gap-2">
            {(site?.languages || []).length > 1 && <button onClick={() => setLang(nextLang(site?.languages, lang))} className="site-nav-link flex items-center gap-1" data-testid="site-lang-toggle"><Globe className="h-4 w-4" />{lang.toUpperCase()}</button>}
            {base === "" && <><Link to="/login" className="site-nav-link hidden sm:inline-flex" data-testid="site-signin">{ui(lang, "Sign in")}</Link><Link to="/signup" className="site-btn-primary !py-2 !px-4 text-sm" data-testid="site-trial">{ui(lang, "Start free trial")}</Link></>}
            <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="site-menu-toggle">{open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
          </div>
        </div>
        {open && <div className="md:hidden border-t px-4 py-3 flex flex-col gap-1 bg-white">{base === "" && <><Link to="/apps" className="site-nav-link" data-testid="site-mobile-apps">{ui(lang, "Apps")}</Link><Link to="/industries" className="site-nav-link" data-testid="site-mobile-industries">{ui(lang, "Industries")}</Link></>}{nav.map((n, i) => <NavA key={i} n={n} />)}</div>}
      </header>
      <main>{children}</main>
      <footer className="site-footer" data-testid="site-footer">
        <div className="site-container py-14 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-2"><div className="font-heading font-semibold text-lg">{site?.name}</div><p className="mt-3 text-sm text-slate-400 max-w-sm">{tx(site?.footer?.tagline, lang)}</p>{site?.footer?.address && <p className="mt-3 text-xs text-slate-500">{site.footer.address}</p>}</div>
          {(site?.footer?.columns || []).map((c, i) => <div key={i}><div className="text-xs font-mono uppercase tracking-widest text-slate-500">{tx(c.title, lang)}</div><ul className="mt-3 space-y-2 text-sm">{(c.links || []).map((l, j) => <li key={j}><Link to={to(l.href)} className="text-slate-300 hover:text-white">{tx(l.label, lang)}</Link></li>)}</ul></div>)}
        </div>
        <div className="border-t border-white/10"><div className="site-container py-5 text-xs text-slate-500 flex flex-wrap gap-3 justify-between"><span>{tx(site?.footer?.legal, lang)}</span><span className="font-mono">Orbis ERP · {pages?.length || 0} pages</span></div></div>
      </footer>
    </div>
  );
}

export default function PublicSite({ tenant = false, catalog = false, children }) {
  const params = useParams();
  const loc = useLocation();
  const siteSlug = tenant ? params.site : null;
  const slug = catalog ? "home" : params.slug || (loc.pathname.endsWith("/careers") ? "careers" : "home");
  const base = tenant ? `/w/${siteSlug}` : "";
  const { data, err } = useSiteData(siteSlug);
  const lang = useSiteLang();
  const setLang = (l) => setSiteLang(l);
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => { setNotFound(false); api.get(`/site/page/${slug}${siteSlug ? `?site=${siteSlug}` : ""}`).then((r) => setPage(r.data)).catch(() => setNotFound(true)); }, [slug, siteSlug]);
  useEffect(() => {
    if (!page || catalog) return;
    document.title = tx(page.seo?.title, lang) || tx(page.title, lang) || data?.site?.name || "Orbis ERP";
    let m = document.querySelector('meta[name="description"]'); if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = tx(page.seo?.description, lang) || "";
    if (loc.hash) setTimeout(() => document.querySelector(loc.hash)?.scrollIntoView({ behavior: "smooth" }), 100);
  }, [page, lang, data, loc.hash]);
  if (err) return <div className="min-h-screen grid place-items-center text-sm font-mono text-slate-500" data-testid="site-error">Site not available</div>;
  return (
    <SiteFrame site={data?.site} pages={data?.pages} lang={lang} setLang={setLang} base={base}>
      {catalog ? children : notFound ? <div className="site-container py-32 text-center"><div className="text-6xl font-heading">404</div><Link to={base || "/"} className="site-btn-primary mt-6 inline-flex">Home</Link></div>
        : page ? <RenderBlocks blocks={page.blocks} lang={lang} plans={page.plans} base={base} site={siteSlug} /> : <div className="min-h-[60vh]" />}
      {siteSlug && <ChatWidget site={siteSlug} />}
    </SiteFrame>
  );
}
